import React, { useState } from 'react';
import { Loader2, Lock, X } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

/**
 * Modal for forced password change
 * Appears when requirePasswordChange is true
 */
const PasswordChangeModal = ({ user, onPasswordChanged }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Client-side validation
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (currentPassword === newPassword) {
      setError('New password must be different');
      return;
    }

    setIsLoading(true);

    try {
      // Call Cloud Function for password change
      const changePassword = httpsCallable(functions, 'changePassword');
      const result = await changePassword({
        currentPassword,
        newPassword,
      });

      if (result.data.success) {
        // Notify parent that password has been changed
        onPasswordChanged();
      }
    } catch (err) {
      console.error('Password change error:', err);
      setError(
        err.message || 
        'Error changing password. Please verify your current password.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="modal-overlay" 
      style={{ 
        zIndex: 9999,
        background: 'rgba(0,0,0,0.8)',
      }}
    >
      <div 
        className="modal-content" 
        style={{ maxWidth: '450px' }}
      >
        <div 
          style={{ 
            textAlign: 'center', 
            marginBottom: '1.5rem' 
          }}
        >
          <div 
            style={{
              width: '60px',
              height: '60px',
              background: '#fff3e0',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem',
            }}
          >
            <Lock size={28} color="#f57c00" />
          </div>
          <h3 style={{ marginBottom: '0.5rem' }}>
            Password Change Required
          </h3>
          <p style={{ fontSize: '0.9rem', color: '#666' }}>
            You must change your temporary password before continuing
          </p>
        </div>

        <form 
          onSubmit={handleSubmit} 
          style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '1rem' 
          }}
        >
          <div>
            <label 
              style={{ 
                fontSize: '0.85rem', 
                color: '#666', 
                marginBottom: '0.5rem', 
                display: 'block' 
              }}
            >
              Current Password (123456)
            </label>
            <input
              className="input-text"
              type="password"
              placeholder="Enter current password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div>
            <label 
              style={{ 
                fontSize: '0.85rem', 
                color: '#666', 
                marginBottom: '0.5rem', 
                display: 'block' 
              }}
            >
              New Password
            </label>
            <input
              className="input-text"
              type="password"
              placeholder="At least 6 characters"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
            />
          </div>

          <div>
            <label 
              style={{ 
                fontSize: '0.85rem', 
                color: '#666', 
                marginBottom: '0.5rem', 
                display: 'block' 
              }}
            >
              Confirm New Password
            </label>
            <input
              className="input-text"
              type="password"
              placeholder="Repeat new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
            />
          </div>

          {error && (
            <div 
              style={{
                color: '#d32f2f',
                fontSize: '0.85rem',
                background: '#ffebee',
                padding: '0.75rem',
                borderRadius: '8px',
                border: '1px solid #ffcdd2',
              }}
            >
              {error}
            </div>
          )}

          <button
            className="btn-primary"
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              justifyContent: 'center',
              marginTop: '0.5rem',
            }}
          >
            {isLoading ? (
              <Loader2 className="animate-spin" />
            ) : (
              'Change Password'
            )}
          </button>
        </form>

        <div 
          style={{
            marginTop: '1rem',
            padding: '0.75rem',
            background: '#e3f2fd',
            borderRadius: '8px',
            fontSize: '0.8rem',
            color: '#1976d2',
          }}
        >
          ℹ️ New password must be different from current password 
          and at least 6 characters long
        </div>
      </div>
    </div>
  );
};

export default PasswordChangeModal;